"use client";

import Link from "next/link";
import { differenceInDays, startOfDay } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { buttonVariants } from "@/components/ui/button";
import ContactFormDialog from "@/components/contactFormDialog";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getNextBillingCycleDate } from "@/features/payments/lib/utils";
import { client } from "@/lib/hono";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";

export default function SidebarAlerts() {
    const { user } = useAuth();
    const hasNegativeBalance = user?.hasNegativeBalance;
    const countdown = user?.negativeBalanceCountdown ?? 0;

    const nextBillingCycle = getNextBillingCycleDate(
        user?.billingCycleStartDate
    );
    const daysUntilBillingCycle = nextBillingCycle
        ? differenceInDays(startOfDay(nextBillingCycle), startOfDay(new Date()))
        : null;
    const inBillingCycleWindow =
        !hasNegativeBalance &&
        daysUntilBillingCycle !== null &&
        daysUntilBillingCycle >= 0 &&
        daysUntilBillingCycle < 30;

    const { data: hasUpcomingRenewal } = useQuery({
        queryKey: ["billing", "has-upcoming-renewal"],
        queryFn: async () => {
            const res = await client.api.billing["has-upcoming-renewal"].$get();
            if (!res.ok) return null;
            const data = await res.json();
            return "hasUpcomingRenewal" in data
                ? data.hasUpcomingRenewal
                : null;
        },
        enabled: inBillingCycleWindow,
    });

    const showBillingCycleAlert =
        inBillingCycleWindow && hasUpcomingRenewal === false;

    if (!hasNegativeBalance && !showBillingCycleAlert) {
        return null;
    }

    return (
        <div className="group-data-[collapsible=icon]:hidden">
            {hasNegativeBalance && (
                <div className="rounded-lg border-2 border-destructive bg-red-50 p-3 text-center text-sm">
                    <p className="text-lg font-semibold text-red-700">
                        {t("platform.sidebar.negative-balance.title")}
                    </p>
                    <p className="mb-3 text-sm text-red-600">
                        {t(
                            "platform.sidebar.negative-balance.description",
                            undefined,
                            { days: countdown }
                        )}
                    </p>
                    <Link
                        href="/settings/pricing"
                        className={cn(
                            buttonVariants({
                                variant: "destructive",
                                size: "xs",
                            }),
                            "w-full"
                        )}
                    >
                        {t("platform.sidebar.negative-balance.cta")}
                    </Link>
                    <ContactFormDialog />
                </div>
            )}
            {showBillingCycleAlert && (
                <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-3 text-center text-sm">
                    <p className="text-lg font-semibold text-blue-900">
                        {t("platform.sidebar.billing-cycle-ending.title")}
                    </p>
                    <p className="mb-3 text-sm text-blue-800">
                        {t(
                            "platform.sidebar.billing-cycle-ending.description",
                            undefined,
                            { days: daysUntilBillingCycle }
                        )}
                    </p>
                    <ContactFormDialog
                        trigger={
                            <button
                                className={cn(
                                    buttonVariants({ size: "xs" }),
                                    "w-full bg-blue-600 text-white hover:bg-blue-700"
                                )}
                            >
                                {t("platform.sidebar.contact-us.trigger")}
                            </button>
                        }
                    />
                </div>
            )}
        </div>
    );
}
